import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Heart, CheckCircle2, XCircle, Clock, PlaneTakeoff, Receipt, BookOpenCheck, CalendarDays, IndianRupee } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ReceiptActions } from "@/components/ReceiptActions";
import { PayOnlineDialog } from "@/components/PayOnlineDialog";
import { verifyCashfreeReturn } from "@/lib/payments";
import { Mail, CreditCard, Camera, Loader2 } from "lucide-react";
import { DailyActivityPanel } from "@/components/DailyActivityPanel";
import { DayReportCard } from "@/components/DayReportCard";
import { WeekReviewCard } from "@/components/WeekReviewCard";
import { AuthImage } from "@/components/AuthImage";

function ChildPhoto({ child, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return toast.error("Choose a JPG, PNG or WEBP photo");
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/portal/children/${child.id}/photo`, fd);
      onUploaded(data.url); toast.success("Photo saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-col items-center gap-1">
      <button type="button" data-testid="portal-child-photo-btn" onClick={() => ref.current?.click()} title={child.photo_url ? "Change photo" : "Add photo"}
        className="relative h-16 w-16 rounded-2xl overflow-hidden shadow group">
        {child.photo_url
          ? <AuthImage testid="portal-child-photo" src={child.photo_url} alt={child.name} className="h-16 w-16 object-cover" />
          : <div className="h-16 w-16 bg-white/20 backdrop-blur flex items-center justify-center text-2xl font-bold">{child.name.charAt(0)}</div>}
        <span className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
        </span>
        {!child.photo_url && <span className="absolute bottom-0.5 right-0.5 h-5 w-5 rounded-full bg-amber-400 flex items-center justify-center"><Camera size={11} /></span>}
      </button>
      {!child.photo_url && <span data-testid="portal-photo-ask" className="text-[10px] text-violet-100">Add photo (optional)</span>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="portal-child-photo-input" />
    </div>
  );
}

export default function ParentPortalPage() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [attendance, setAttendance] = useState(null);
  const [fees, setFees] = useState([]);
  const [receiptInv, setReceiptInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [payInv, setPayInv] = useState(null);
  const [feesTick, setFeesTick] = useState(0);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("online_payment");
    if (!pid) return;
    window.history.replaceState({}, "", "/portal");
    verifyCashfreeReturn(pid)
      .then((r) => {
        if (r.status === "paid") toast.success(`Payment of ₹${Number(r.amount).toFixed(0)} received. Receipt ${r.receipt_no}`);
        else toast.error("Payment was not completed.");
        setFeesTick((t) => t + 1);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  async function emailMe() {
    setEmailing(true);
    try { const { data } = await api.post("/digest/me"); toast.success(`Summary sent to ${data.to}`); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailing(false); }
  }

  async function loadChildren() {
    const { data } = await api.get("/parent/children");
    setChildren(data);
    if (data[0]) setChildId(data[0].id);
  }
  useEffect(() => { loadChildren().catch((e) => toast.error(formatApiError(e))); }, []);

  useEffect(() => {
    if (!childId) return;
    const [y, m] = month.split("-");
    api.get("/parent/attendance", { params: { student_id: childId, year: parseInt(y), month: parseInt(m) } }).then((r) => setAttendance(r.data));
    api.get("/grades", { params: { student_id: childId } }).then((r) => setGrades(r.data));
  }, [childId, month]);

  useEffect(() => {
    api.get("/parent/fees").then((r) => setFees(r.data.invoices.filter((i) => !childId || i.student_id === childId)));
  }, [childId, feesTick]);

  useEffect(() => {
    if (!childId) return;
    const child = children.find((c) => c.id === childId);
    if (!child) return;
    api.get("/timetable", { params: { branch_id: child.branch_id, class_name: child.class_name, section: child.section, academic_year: "2025-26" } })
      .then((r) => setTimetable(r.data?.slots || []))
      .catch(() => setTimetable([]));
  }, [childId, children]);

  async function openReceipt(inv) {
    setReceiptInv(inv);
    const { data } = await api.get(`/fees/invoices/${inv.id}/payments`);
    setPayments(data);
  }

  const child = useMemo(() => children.find((c) => c.id === childId), [children, childId]);

  if (children.length === 0) {
    return (
      <AppShell>
        <div className="mb-6">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Parent Portal</h1>
          <p className="text-slate-500 mt-1.5">Your children appear here once a staff member links them to your account.</p>
        </div>
        <Card className="border-dashed border-2 border-violet-200">
          <CardContent className="py-16 text-center">
            <Heart className="mx-auto text-violet-400" size={44} />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">No linked children yet</h3>
            <p className="text-slate-500 text-sm mt-1">Please contact the school office to connect your child's record to this parent account.</p>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Parent Portal</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
            Hi {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1.5">{children.length} linked {children.length === 1 ? "child" : "children"} • Everything about them in one view.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <Button data-testid="email-me-summary-btn" variant="outline" onClick={emailMe} disabled={emailing} className="border-violet-200 text-violet-700 bg-white">
          <Mail size={15} className="mr-1.5" /> {emailing ? "Sending…" : "Email me this month's summary"}
        </Button>
        <Select value={childId} onValueChange={setChildId}>
          <SelectTrigger data-testid="parent-child-select" className="w-full sm:w-72 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} • {c.class_name}/{c.section}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>

      {child && (
        <Card className="border-violet-100 bg-gradient-to-br from-violet-600 to-purple-700 text-white mb-5 overflow-hidden">
          <CardContent className="p-6 flex items-center gap-5">
            <ChildPhoto child={child} onUploaded={(url) => setChildren((cs) => cs.map((c) => (c.id === child.id ? { ...c, photo_url: url } : c)))} />
            <div>
              <div className="font-display text-2xl font-bold">{child.name}</div>
              <div className="text-violet-200 text-sm mt-1">{child.admission_number} • {child.class_name} / Section {child.section}</div>
              <div className="text-violet-200 text-xs mt-0.5">Academic year {child.academic_year}</div>
              {child.contact_teacher_name && <div data-testid="portal-contact-teacher" className="text-white text-xs mt-1 font-semibold">Contact teacher: {child.contact_teacher_name}</div>}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "attendance"} className="w-full">
        <TabsList className="bg-violet-100">
          <TabsTrigger value="activity" data-testid="portal-tab-activity" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Daily Activity</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="portal-tab-attendance" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Attendance</TabsTrigger>
          <TabsTrigger value="fees" data-testid="portal-tab-fees" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Fees</TabsTrigger>
          <TabsTrigger value="grades" data-testid="portal-tab-grades" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Report Card</TabsTrigger>
          <TabsTrigger value="timetable" data-testid="portal-tab-timetable" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Timetable</TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="mt-5">
          {child ? <><WeekReviewCard student={child} /><DayReportCard student={child} /><DailyActivityPanel student={child} /></> : <p className="text-sm text-slate-500">Choose a child.</p>}
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance" className="mt-5">
          <div className="flex items-end gap-3 mb-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Month</label>
              <input data-testid="portal-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="block mt-1.5 border border-slate-200 rounded-md px-3 py-2 input-focus" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <AttStat label="Present" value={attendance?.counts?.Present ?? 0} color="text-emerald-700 bg-emerald-50 border-emerald-100" Icon={CheckCircle2} />
            <AttStat label="Absent" value={attendance?.counts?.Absent ?? 0} color="text-rose-700 bg-rose-50 border-rose-100" Icon={XCircle} />
            <AttStat label="Late" value={attendance?.counts?.Late ?? 0} color="text-amber-700 bg-amber-50 border-amber-100" Icon={Clock} />
            <AttStat label="Leave" value={attendance?.counts?.Leave ?? 0} color="text-blue-700 bg-blue-50 border-blue-100" Icon={PlaneTakeoff} />
          </div>
          {attendance?.days && Object.keys(attendance.days).length > 0 ? (
            <Card className="border-violet-100">
              <CardContent className="p-5">
                <div className="grid grid-cols-7 gap-1.5">
                  {Object.entries(attendance.days).sort().map(([d, status]) => (
                    <div key={d} className={`rounded-lg border text-center p-2 text-xs ${dayColor(status)}`}>
                      <div className="font-bold text-slate-900">{d.split("-")[2]}</div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold mt-0.5">{status.charAt(0)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-10 text-center text-slate-500">No attendance records for {month}.</CardContent></Card>
          )}
        </TabsContent>

        {/* Fees */}
        <TabsContent value="fees" className="mt-5">
          {fees.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-10 text-center text-slate-500">No invoices yet.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {fees.map((i) => (
                <Card key={i.id} data-testid={`parent-invoice-${i.receipt_no}`} className="border-violet-100">
                  <CardContent className="p-5 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-display font-bold text-slate-900">{i.period}</div>
                      <div className="text-xs text-slate-500 font-mono">{i.receipt_no} • Due {i.due_date}</div>
                      <div className="flex gap-1.5 mt-2">
                        <Badge className={i.status === "paid" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : i.status === "partial" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-rose-100 text-rose-800 hover:bg-rose-100"}>
                          {i.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl font-bold text-slate-900">₹{i.total.toFixed(0)}</div>
                      {i.balance > 0 && <div className="text-xs text-rose-600 mt-0.5">Balance ₹{i.balance.toFixed(0)}</div>}
                      <Button data-testid={`parent-receipt-${i.receipt_no}`} onClick={() => openReceipt(i)} variant="outline" size="sm" className="mt-2 border-violet-200 text-violet-700 hover:bg-violet-50">
                        <Receipt size={12} className="mr-1" /> Receipt
                      </Button>
                      {i.balance > 0 && (
                        <Button data-testid={`parent-pay-${i.receipt_no}`} onClick={() => setPayInv(i)} size="sm" className="mt-2 ml-2 bg-emerald-600 hover:bg-emerald-700">
                          <CreditCard size={12} className="mr-1" /> Pay online
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Grades */}
        <TabsContent value="grades" className="mt-5">
          {grades.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-10 text-center text-slate-500">No grades recorded yet.</CardContent></Card>
          ) : (
            <Card className="border-violet-100 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="bg-violet-50 text-slate-700">
                      <tr><th className="px-4 py-3 text-left font-semibold">Subject</th><th className="px-3 py-3 text-left font-semibold">Term</th><th className="text-right px-3 py-3 font-semibold">Marks</th><th className="text-right px-3 py-3 font-semibold">%</th><th className="text-center px-3 py-3 font-semibold">Grade</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {grades.map((g) => (
                        <tr key={g.id}>
                          <td className="px-4 py-3 font-semibold">{g.subject}</td>
                          <td className="px-3 py-3 text-slate-600">{g.term}</td>
                          <td className="text-right px-3 py-3 font-semibold">{g.marks}/{g.max_marks}</td>
                          <td className="text-right px-3 py-3">{g.percent}%</td>
                          <td className="text-center px-3 py-3"><Badge className={g.grade === "A+" || g.grade === "A" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-violet-100 text-violet-800 hover:bg-violet-100"}>{g.grade}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Timetable */}
        <TabsContent value="timetable" className="mt-5">
          {timetable.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-10 text-center text-slate-500">Timetable not published yet.</CardContent></Card>
          ) : (
            <Card className="border-violet-100 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                  <thead className="bg-violet-50 text-slate-700">
                    <tr>
                      <th className="px-3 py-3 text-left font-semibold">Period</th>
                      {["Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <th key={d} className="px-3 py-3 text-left font-semibold">{d}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[1,2,3,4,5,6,7,8].map((p) => (
                      <tr key={p}>
                        <td className="px-3 py-3 bg-slate-50 font-semibold">#{p}</td>
                        {["Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => {
                          const slot = timetable.find((s) => s.day === d && s.period === p);
                          return (
                            <td key={d} className="px-3 py-3 align-top">
                              {slot ? (<><div className="font-semibold text-slate-900">{slot.subject}</div>{slot.teacher_name && <div className="text-xs text-slate-500">{slot.teacher_name}</div>}</>) : <span className="text-slate-300">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <PayOnlineDialog invoice={payInv} onClose={() => setPayInv(null)} onPaid={() => setFeesTick((t) => t + 1)} />

      {/* Receipt dialog */}
      <Dialog open={!!receiptInv} onOpenChange={(v) => { if (!v) { setReceiptInv(null); setPayments([]); } }}>
        <DialogContent>
          {receiptInv && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Receipt size={20} /> {receiptInv.receipt_no}</DialogTitle></DialogHeader>
              <div className="rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4 mt-2">
                <div className="text-sm text-slate-600">{receiptInv.period} • Due {receiptInv.due_date}</div>
                <div className="mt-3 space-y-1">
                  {receiptInv.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm"><span>{it.name}</span><span className="font-semibold">₹{Number(it.amount).toFixed(0)}</span></div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-violet-200 flex justify-between items-center">
                  <span className="text-sm">Balance</span>
                  <span className="font-display text-xl font-bold text-rose-700">₹{receiptInv.balance.toFixed(0)}</span>
                </div>
              </div>
              {payments.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">Payments</div>
                  {payments.map((p) => (
                    <div key={p.id} className="flex justify-between text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-1">
                      <span><span className="font-mono text-xs text-slate-500">{p.receipt_no}</span> • {new Date(p.received_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} • {p.mode.toUpperCase()}</span>
                      <span className="font-semibold text-emerald-700">₹{p.amount.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
              )}
              <ReceiptActions invoiceId={receiptInv.id} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function AttStat({ label, value, color, Icon }) {
  return (
    <div className={`rounded-xl border p-3.5 ${color}`}>
      <div className="flex items-center gap-2"><Icon size={15} /><span className="text-xs uppercase tracking-wider font-semibold">{label}</span></div>
      <div className="font-display text-2xl font-bold mt-1.5">{value}</div>
    </div>
  );
}

function dayColor(status) {
  if (status === "Present") return "bg-emerald-50 border-emerald-200 text-emerald-700";
  if (status === "Absent") return "bg-rose-50 border-rose-200 text-rose-700";
  if (status === "Late") return "bg-amber-50 border-amber-200 text-amber-700";
  return "bg-blue-50 border-blue-200 text-blue-700";
}
